// Sdílený stav klienta Kolonie a dotazy nad pohledem (co smím, popisy míst). Používá zvuková i vizuální vrstva.
import { COSTS, pips, RESOURCES, type Bag, type KolonieAction, type KolonieView, type Pending, type Res } from "../../../games/kolonie/meta.ts";
import { bankRate, distanceOk, legalRoads, legalSettlements } from "../../../games/kolonie/logic.ts";
import { TOPOLOGY as T, otherEnd } from "../../../games/kolonie/topology.ts";
import { bagText, hexTextIns, portText, type ProbStyle } from "./texts.ts";

export type PickKind = "setupSettlement" | "setupRoad" | "road" | "freeRoad" | "settlement" | "city" | "robber";

export interface Model {
  me: string;
  view: KolonieView | null;
  send(action: KolonieAction): void;
  /** Kurzor zvukové vrstvy (roh) a vybraná hrana. */
  cursor: number;
  edge: number | null;
  /** Výběr místa na mapě (dotyk), když hráč staví nebo přesouvá piráty. */
  pick: PickKind | null;
  /** Historie hlášení (klávesa L). */
  log: string[];
  /** Co se stalo od mého posledního tahu (shrnutí na začátku tahu). */
  since: string[];
  prob: ProbStyle;
  confirmBuild: boolean;
}

export const myIndex = (v: KolonieView) => v.me;

export function name(v: KolonieView, p: number): string {
  return p === v.me ? "ty" : v.players[p].name;
}

export function myPending(v: KolonieView): Pending[] {
  if (v.me === null) return [];
  return v.pending.filter((p) => p.player === v.players[v.me!].id);
}

export function afford(v: KolonieView, cost: Bag): boolean {
  return !!v.hand && RESOURCES.every((r) => v.hand![r] >= (cost[r] ?? 0));
}

export function missing(v: KolonieView, cost: Bag): Bag {
  const out: Bag = {};
  for (const r of RESOURCES) {
    const m = (cost[r] ?? 0) - (v.hand?.[r] ?? 0);
    if (m > 0) out[r] = m;
  }
  return out;
}

/** Roh, u kterého při rozestavění ještě nevede můj tunel (poslední postavená základna). */
export function setupCorner(v: KolonieView): number {
  return v.corners.findIndex((b, c) => b?.owner === v.me && !T.corners[c].edges.some((e) => v.edges[e] === v.me));
}

/** Legální místa pro daný výběr. */
export function targets(v: KolonieView, kind: PickKind): number[] {
  const me = v.me!;
  switch (kind) {
    case "setupSettlement":
      return T.corners.map((_, c) => c).filter((c) => distanceOk(v, c));
    case "setupRoad": {
      const c = setupCorner(v);
      return c < 0 ? [] : T.corners[c].edges.filter((e) => v.edges[e] === null);
    }
    case "road":
    case "freeRoad":
      return legalRoads(v, me);
    case "settlement":
      return legalSettlements(v, me);
    case "city":
      return v.corners.map((b, c) => (b && b.owner === me && !b.city ? c : -1)).filter((c) => c >= 0);
    case "robber":
      return T.hexes.map((_, h) => h).filter((h) => h !== v.robber);
  }
}

/** Součet teček sousedních čísel („síla“ rohu). */
export function cornerStrength(v: KolonieView, c: number): number {
  return T.corners[c].hexes.reduce((n, h) => n + (v.hexes[h].terrain === "krater" ? 0 : pips(v.hexes[h].number)), 0);
}

/** „kovem na osmičce, vodou na šestce, mořem“ */
export function cornerNeighbors(v: KolonieView, c: number, style: ProbStyle): string {
  const parts = T.corners[c].hexes.map((h) => hexTextIns(v.hexes[h], style) + (h === v.robber ? " s piráty" : ""));
  if (T.corners[c].hexes.length < 3) parts.push("mořem");
  return parts.join(", ");
}

export function cornerPort(v: KolonieView, c: number): string | null {
  const port = v.ports.find((p) => T.edges[p.edge].corners.includes(c));
  return port ? portText(port.type) : null;
}

/** Co je na rohu: „volný“, „tvoje základna“, „město: Jirka“. */
export function cornerContent(v: KolonieView, c: number): string {
  const b = v.corners[c];
  if (!b) return distanceOk(v, c) ? "volný" : "volný, ale příliš blízko jiné stavby";
  const what = b.city ? "město" : "základna";
  return b.owner === v.me ? `tvoje ${what}` : `${what}: ${v.players[b.owner].name}`;
}

/** Celý popis místa pro seznam: „sousedí s kovem na osmičce, vodou na šestce; síla 9; stanice 3 za 1“. */
export function cornerSummary(v: KolonieView, c: number, style: ProbStyle): string {
  const parts = [`sousedí s ${cornerNeighbors(v, c, style)}`, `síla ${cornerStrength(v, c)}`];
  const port = cornerPort(v, c);
  if (port) parts.push(port);
  return parts.join("; ");
}

/** Kdo má tunel na hraně: „tvůj tunel“, „tunel: Jirka“, null = volná. */
export function edgeOwner(v: KolonieView, e: number): string | null {
  const o = v.edges[e];
  if (o === null) return null;
  return o === v.me ? "tvůj tunel" : `tunel: ${v.players[o].name}`;
}

/** Popis hrany pro seznam tunelů: kam vede a co je tam za místo. */
export function edgeSummary(v: KolonieView, e: number, style: ProbStyle): string {
  const [a, b] = T.edges[e].corners;
  const mine = (c: number) => v.corners[c]?.owner === v.me || T.corners[c].edges.some((x) => x !== e && v.edges[x] === v.me);
  const from = mine(a) ? a : b;
  const to = otherEnd(e, from);
  const target = distanceOk(v, to) ? `k volnému místu: ${cornerSummary(v, to, style)}` : `k rohu u ${cornerNeighbors(v, to, style)}`;
  return `od ${cornerNeighbors(v, from, style)} ${target}`;
}

export function rateText(v: KolonieView, r: Res): number {
  return bankRate(v, v.me!, r);
}

export function costText(cost: Bag): string {
  return bagText(cost);
}

export { COSTS, T };

/** Krátká nápověda „co teď smím“ podle fáze (vizuální i zvuková vrstva, průvodce). */
export function whatNow(v: KolonieView): string {
  if (v.me === null) return "Díváš se na hru.";
  if (v.phase === "over") return "Hra skončila.";
  const mine = myPending(v);
  const turnName = v.players[v.turn].name;
  if (!mine.length) {
    if (v.phase === "discard") return "Čeká se, až ostatní odhodí karty.";
    return `Na tahu je ${turnName}.`;
  }
  const p = mine[0];
  switch (p.kind) {
    case "setupSettlement":
      return "Postav základnu. Nejlepší jsou místa u šestek a osmiček a u různých surovin.";
    case "setupRoad":
      return "Postav tunel vedle nové základny.";
    case "roll":
      return v.devHand.some((d) => d.kind === "hlidka" && !d.fresh) && !v.devPlayedThisTurn
        ? "Hoď kostkami. Před hodem můžeš zahrát hlídkovou loď."
        : "Hoď kostkami.";
    case "discard":
      return `Padla sedmička: odhoď ${p.count} ${p.count! >= 5 ? "karet" : "karty"}.`;
    case "robber":
      return "Přesuň piráty na jiné pole.";
    case "steal":
      return "Vyber, komu ukradneš kartu.";
    case "roadBuilding":
      return `Postav ${p.count === 1 ? "ještě 1 tunel" : "2 tunely"} zdarma.`;
    case "tradeResponse":
      return "Máš nabídku obchodu: přijmi ji, nebo odmítni.";
    case "main": {
      const can: string[] = [];
      if (afford(v, COSTS.mesto) && targets(v, "city").length) can.push("město");
      if (afford(v, COSTS.zakladna) && targets(v, "settlement").length) can.push("základnu");
      if (afford(v, COSTS.tunel) && targets(v, "road").length) can.push("tunel");
      if (afford(v, COSTS.vyzkum) && v.devDeck > 0) can.push("kartu výzkumu");
      const build = can.length ? `Můžeš postavit: ${can.join(", ")}.` : "Na stavbu ti teď nic nestačí.";
      return `${build} Můžeš měnit s bankou nebo s hráči, nebo ukončit tah.`;
    }
  }
  return "";
}
